import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, FileText, StickyNote, BellRing, MessageCircle, Trash2 } from "lucide-react";
import { fmtDate } from "@/admin/adminLabels";
import { toast } from "sonner";

const TYPE_META = {
  call: { label: "Chiamata", icon: Phone, color: "#00B4D8" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  email: { label: "Email", icon: Mail, color: "#0B192C" },
  quote: { label: "Preventivo", icon: FileText, color: "#D4AF37" },
  note: { label: "Nota", icon: StickyNote, color: "#475569" },
  reminder: { label: "Promemoria", icon: BellRing, color: "#DC2626" },
};

export default function ActivityTimeline({ leadId, activities, onChanged }) {
  const [type, setType] = useState("call");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!description.trim()) return toast.error("Inserisci una descrizione");
    setSaving(true);
    try {
      const body = { type, description };
      if (type === "reminder" && dueDate) body.due_date = new Date(dueDate).toISOString();
      await api.post(`/admin/leads/${leadId}/activities`, body);
      setDescription(""); setDueDate("");
      onChanged?.();
      toast.success("Attività aggiunta");
    } catch (e) {
      toast.error("Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (aid) => {
    await api.delete(`/admin/leads/${leadId}/activities/${aid}`);
    onChanged?.();
  };

  const sorted = [...(activities || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
      <p className="text-xs uppercase tracking-wider text-ink font-semibold">Timeline attività</p>

      {/* Add new */}
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="act-type" className="h-10 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {type === "reminder" && (
            <Input
              data-testid="act-due"
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="h-10 text-xs col-span-2"
            />
          )}
        </div>
        <Textarea
          data-testid="act-description"
          rows={2}
          placeholder="Descrivi l'attività (es. 'chiamata effettuata, cliente disponibile')"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Button
          data-testid="act-add"
          onClick={add}
          disabled={saving}
          size="sm"
          className="bg-navy text-white hover:bg-[#06101f] h-9"
        >
          {saving ? "…" : "Aggiungi attività"}
        </Button>
      </div>

      {/* Timeline */}
      <div className="mt-5 space-y-3">
        {sorted.length === 0 && <p className="text-sm text-ink">Nessuna attività registrata.</p>}
        {sorted.map((a) => {
          const m = TYPE_META[a.type] || TYPE_META.note;
          return (
            <div key={a.id} data-testid={`act-row-${a.id}`} className="flex gap-3 group">
              <div className="flex flex-col items-center">
                <span className="h-7 w-7 rounded-full flex items-center justify-center text-white" style={{ background: m.color }}>
                  <m.icon className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1 w-px bg-[#E2E8F0]" />
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-medium text-navy">{m.label}</p>
                  <button onClick={() => remove(a.id)} className="text-ink/50 hover:text-red-500 opacity-0 group-hover:opacity-100 transition">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-ink">{fmtDate(a.date)} · {a.author || "admin"}</p>
                <p className="text-sm text-navy mt-1 whitespace-pre-wrap">{a.description}</p>
                {a.due_date && (
                  <p className="text-xs text-gold mt-1">Promemoria per: {fmtDate(a.due_date)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
